import { useAuth } from "../auth";
import { useI18n } from "../i18n";

/** (Task Z) Illustration: a tablet at the front desk running checkout (✓) next to a phone (✕). Inline SVG, no assets. */
export function TabletIllustration({ size = 220 }: { size?: number }) {
  const { t } = useI18n();
  return (
    <svg className="tablet-illu" width={size} height={Math.round(size * 0.55)} viewBox="0 0 220 120" role="img" aria-label={t("tz.tip.alt")}>
      {/* desk */}
      <rect x="6" y="104" width="208" height="8" rx="4" fill="#d9e6e3" />
      {/* tablet on a stand, landscape */}
      <path d="M62 104 l10 -14 h16 l10 14 z" fill="#b7cfca" />
      <rect x="22" y="16" width="118" height="78" rx="10" fill="#1f2a28" />
      <rect x="29" y="23" width="104" height="64" rx="5" fill="#f4faf8" />
      <rect x="34" y="28" width="58" height="8" rx="3" fill="#cfe3df" />
      {[0, 1, 2].map((r) => [0, 1, 2].map((c) => <rect key={r + "-" + c} x={34 + c * 20} y={40 + r * 14} width="17" height="11" rx="3" fill="#e1eeeb" />))}
      <rect x="98" y="28" width="30" height="40" rx="4" fill="#e1eeeb" />
      <rect x="98" y="72" width="30" height="11" rx="4" fill="#00706B" />
      <circle cx="132" cy="22" r="11" fill="#00706B" />
      <path d="M127 22 l4 4 l7 -8" stroke="#fff" strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      {/* phone */}
      <rect x="164" y="34" width="38" height="70" rx="8" fill="#1f2a28" />
      <rect x="168" y="40" width="30" height="56" rx="4" fill="#f4faf8" />
      <rect x="172" y="46" width="22" height="6" rx="2" fill="#e1eeeb" />
      <rect x="172" y="56" width="22" height="6" rx="2" fill="#e1eeeb" />
      <rect x="172" y="66" width="22" height="6" rx="2" fill="#e1eeeb" />
      <circle cx="202" cy="36" r="11" fill="#c2410c" />
      <path d="M197.5 31.5 l9 9 M206.5 31.5 l-9 9" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" />
    </svg>
  );
}

/** Setup wizard / create-cashier tip: "Use an iPad or Android tablet for the cashier", 4 short steps. */
export function TabletTip({ compact = false }: { compact?: boolean }) {
  const { t } = useI18n();
  return (
    <aside className={"tablet-tip" + (compact ? " compact" : "")} data-testid="tablet-tip">
      <TabletIllustration size={compact ? 160 : 220} />
      <div className="stack">
        <strong>{t("tz.tip.title")}</strong>
        <ol className="tablet-steps">
          <li>{t("tz.tip.s1")}</li>
          <li>{t("tz.tip.s2")}</li>
          <li>{t("tz.tip.s3")}</li>
          {!compact && <li>{t("tz.tip.s4")}</li>}
        </ol>
      </div>
    </aside>
  );
}

/** A cashier account on a phone: friendly full screen instead of the POS (checkout needs a tablet). */
export function TabletRequired() {
  const { t } = useI18n();
  const { me, logout } = useAuth();
  return (
    <div className="tablet-required" data-testid="tablet-required">
      <div className="card stack center-text">
        <TabletIllustration size={240} />
        <h1>{t("tz.tab.title")}</h1>
        <p>{t("tz.tab.body")}</p>
        <p className="muted">{t("tz.tab.how")}</p>
        {me?.user.email && <p className="muted small">{me.user.email}</p>}
        <p className="hint">{t("tz.tab.staffHint")}</p>
        <button className="btn btn-primary btn-lg" onClick={() => void logout()}>{t("common.signOut")}</button>
      </div>
    </div>
  );
}
